/**
 * Copyright Highway9 Networks Inc.
 */

import React, { useEffect, useState, useMemo } from "react";
import { text } from "../../components/TextResources";
import { Alert, Box, Checkbox, FormControlLabel, Grid, Input, SearchSelector, SummaryTemplate } from "@h9/ui-lib";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { utilityActions } from "~/store/context/utilitySlice";
import timezones from "~/assets/timezones.json";
import moment from "moment-timezone";
import { settingsAirgapEnabled } from "~/store/context/settingsSlice";
import { useCurrentRole } from "~/hooks/useCurrentRole";
export interface EditProfileUser {
  name?: string;
  email?: string;
  authType?: string[];
  timeZone?: string;
  timezone?: string;
  oldPassword?: string;
  newPassword?: string;
  repeatPassword?: string;
}

interface GeneralDetailsProps {
  user: EditProfileUser;
  setUser: React.Dispatch<React.SetStateAction<EditProfileUser>>;
  timezone?: string;
  changeTimezone: (tz: string | undefined) => void;
  setErrorsAndWarnings?: (errors: unknown[], warnings?: unknown[]) => void;
}

interface TimeZoneOption {
  label: string;
  value: string | null;
  group: string;
}

interface TimeZoneProps {
  timezone?: string;
  onChange: (value: string | undefined) => void;
}

export function GeneralDetails(props: GeneralDetailsProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const { user, setUser, timezone, changeTimezone } = props;
  const { name, email, authType } = user;

  // biome-ignore lint/correctness/useExhaustiveDependencies: preserve existing hook dependency list
  useEffect(() => {
    const tz = user?.timeZone ?? user?.timezone;
    if (tz) {
      dispatch(utilityActions.setTimeZone(tz));
    }
  }, [user]);

  const airGapEnabled = useAppSelector(settingsAirgapEnabled);
  const [changePassword, setChangePassword] = useState(false);
  const [errors, setErrors] = useState("");
  const { isTenant } = useCurrentRole();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const type = e.target.name;
    const value = e.target.value;
    setUser((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setChangePassword(event.target.checked);
  };

  useEffect(() => {
    if (!user?.oldPassword || !user?.newPassword || !user?.repeatPassword) {
      setErrors("");
    } else if (user?.newPassword !== user?.repeatPassword) {
      setErrors("Passwords do not match");
    } else {
      setErrors("");
    }
  }, [user?.newPassword, user?.repeatPassword, user?.oldPassword]);

  return (
    <Grid container>
      <Box sx={{ marginTop: 2, width: "80%" }}>
        <SummaryTemplate
          title="General Details"
          data={[
            { key: "User Name", value: name },
            { key: "Email", value: email },
            { key: "Auth Type", value: authType?.join(", ") },
          ]}
        />
      </Box>

      <Grid xs={12}>
        <TimeZone timezone={timezone} onChange={changeTimezone} />

        {authType?.includes("PASSWORD") && (isTenant || airGapEnabled) && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={changePassword}
                  onChange={handleChangePassword}
                  name="changePassword"
                  color="primary"
                />
              }
              label="Change Password"
            />
          </>
        )}

        {changePassword && (
          <>
            {errors && (
              <Alert severity="error" style={{ marginBottom: 10, width: "100%" }}>
                {errors}
              </Alert>
            )}
            <Input
              variant="standard"
              label={text.newPassword}
              value={user.newPassword ?? ""}
              name="newPassword"
              type={"password"}
              onChange={onChange}
              fullWidth
              required
    
         
            />
            <Input
              variant="standard"
              label={text.repeatPassword}
              value={user.repeatPassword ?? ""}
              name="repeatPassword"
              type={"password"}
              onChange={onChange}
              fullWidth
              required
            />
          </>
        )}
      </Grid>
    </Grid>
  );
}

function TimeZone(props: TimeZoneProps): React.ReactElement {
  const { timezone, onChange } = props;

  const timeZones = useMemo(
    () =>
      Object.entries(timezones as Record<string, string>).map(([tz, name]) => ({
        label: name,
        value: tz,
        group: "All",
      })),
    []
  );

  const systemTimeZone = useMemo(() => moment.tz.guess(), []);

  const current = useMemo(
    () =>
      ({
        ...timeZones.find((zone) => zone.value === systemTimeZone),
        group: "System",
        value: null,
      }) as TimeZoneOption,
    [timeZones, systemTimeZone]
  );

  return (
    <>
      <SearchSelector
        label="Timezone"
        value={timeZones.find((zone) => zone.value === (timezone || systemTimeZone)) ?? undefined}
        options={[current, ...timeZones]}
        onChange={(zone) => onChange((zone as TimeZoneOption)?.value ?? undefined)}
        style={{ background: "transparent", color: "black", border: "1px solid #ccc" }}
      />
    </>
  );
}
